import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDropDown } from "@mui/icons-material";
import { Button, Divider, MenuItem, MenuList, Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Typography from "@common/components/Typography";
import { LocalReport } from "@services/reports/types";

export type SelectPathEntity = Partial<LocalReport> &
  Pick<LocalReport, "path" | "title">;
export type InstanceSelectorProps = {
  label?: string;
  instance?: SelectPathEntity;
  instances?: SelectPathEntity[];
  onChange?: (value: LocalReport) => void;
};

const InstanceSelector = (
  props: React.PropsWithChildren<InstanceSelectorProps>
) => {
  const { label, instance, instances, onChange = () => {} } = props;
  const [open, setOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { left, top } = buttonRef?.current
    ? buttonRef?.current?.getBoundingClientRect().toJSON()
    : 0;

  const listener = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node))
      setOpen(false);
  };
  useEffect(() => {
    addEventListener("mousedown", listener);
    return () => removeEventListener("mousedown", listener);
  }, []);

  const handleClickItem = (item: SelectPathEntity) => {
    onChange({ ...item });
    setOpen(false);
  };

  return (
    <Grid
      container
      direction="row"
      sx={{
        alignItems: "center",
        width: "fit-content",
      }}
    >
      <Grid>
        <Grid
          container
          direction="row"
          sx={{ width: '100%', alignItems: "center",
            justifyContent: "flex-start", }}
        >
          {label && (
            <Grid>
              <Typography
                sx={(theme) => ({
                  color: theme.palette.common.white,
                })}
              >
                {t(label).toLocaleUpperCase()}
              </Typography>
            </Grid>
          )}
          {instances !== undefined && instance !== undefined && label && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                "&.MuiDivider-root": {
                  marginLeft: "1rem",
                  marginRight: "0.5rem",
                },
              }}
            />
          )}
          {instances !== undefined && instance !== undefined && (
            <Grid>
              <Button
                ref={buttonRef}
                endIcon={<ArrowDropDown />}
                onClick={() => setOpen(true)}
                sx={(theme) => ({
                  "&.MuiButton-root": {
                    color: theme.palette.common.white,
                  },
                })}
                data-testid="assets-menu-button"
              >
                <Typography>{t(instance.title).toLocaleUpperCase()}</Typography>
              </Button>
              {open && (
                <Grid
                  container
                  ref={listRef}
                  sx={(theme) => ({
                    left: left - 8,
                    top: top + 40,
                    zIndex: 7,
                    position: "absolute",
                    borderBottom: "unset",
                    "& .MuiPaper-root": {
                      backgroundColor: theme.palette.background.default,
                      overflow: "hidden",
                      borderRadius: theme.radius.medium,
                      border: "1px solid",
                    },
                    "&.MuiGrid2-root": {
                      width: "auto",
                    },
                    "& .MuiList-root": {
                      padding: 0,
                    },
                    "& .MuiMenuItem-root": {
                      padding: `${theme.spacing(1)} ${theme.spacing(2.8)}`,
                      "&:hover": {
                        backgroundColor: theme.palette.common.grey.dark,
                      },
                    },
                  })}
                  direction="column"
                >
                  <Paper>
                    <MenuList>
                      {instances.map((inst) => (
                        <MenuItem
                          key={inst.title}
                          value={inst.title}
                          onClick={() => handleClickItem(inst)}
                          sx={(theme) => ({
                            "&.MuiMenuItem-root": {
                              backgroundColor:
                                inst === instance
                                  ? theme.palette.common.white
                                  : "inherit",
                              color:
                                inst === instance
                                  ? theme.palette.common.black
                                  : "inherit",
                            },
                          })}
                        >
                          {t(inst.title)}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InstanceSelector;
